/**
 * ModalMyBooks.jsx
 * Десктопное модальное окно «Мои книги» — список сохранённых книг
 * с возможностью подгрузить любую и продолжить редактирование.
 */

import { useEffect, useState, useContext } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Box from "@mui/joy/Box";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import Button from "@mui/joy/Button";
import IconButton from "@mui/joy/IconButton";
import CircularProgress from "@mui/joy/CircularProgress";
import Chip from "@mui/joy/Chip";
import Input from "@mui/joy/Input";
import Checkbox from "@mui/joy/Checkbox";
import { Modal, ModalDialog, ModalOverflow } from "@mui/joy";

import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import PhotoLibraryRoundedIcon from "@mui/icons-material/PhotoLibraryRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import ShoppingCartCheckoutRoundedIcon from "@mui/icons-material/ShoppingCartCheckoutRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import AspectRatioRoundedIcon from "@mui/icons-material/AspectRatioRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";

import { bookApi } from "../../api.js";
import { bookFormatLabel } from "../../utils/bookFormatLabel.js";
import { LayoutContext } from "../../context.jsx";
import { isAdminViewMode } from "../../adminViewMode.js";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import BookActionsInfoModal from "./BookActionsInfoModal.jsx";


// ─── Мини-превью обложки для списка ──────────────────────────────────────────

export function MiniCoverPreview({ book, width = 56, height = 72 }) {
  const bgColor = book.cover_bg_color || "#834A90";
  const titleColor = book.cover_title_color || "#fff";
  const accentColor = book.cover_accent_color || "rgba(255,255,255,0.7)";
  const coverText = book.cover_text || "";
  const coverYear = book.cover_year || "";
  const stickerUrl = book.cover_template_data?.image_url ?? null;

  // Макеты сторон обложки (по умолчанию: перед — стикер, зад — текст+фото)
  const frontTag = book.front_cover_layout_data?.tag || "sticker";
  const backTag  = book.back_cover_layout_data?.tag  || "classic";
  const backKind = book.back_cover_layout_data?.kind || "";
  const backIsGrid  = backTag === "classic" || backTag === "grid_photo" || backTag === "grid" || backKind === "grid_photo";
  const backIsPhoto = backTag === "photo";
  const backIsText  = backTag === "text";
  const backIsMinimal = backTag === "minimal";
  const frontIsPhoto  = frontTag === "photo";

  // Фото обложки: 0–3 — сетка задней, 4 — фото передней (макет "photo")
  const backPhotos = {};
  (book.cover_photos || []).forEach(cp => {
    backPhotos[cp.grid_index] = cp.image_url;
  });
  const frontPhotoUrl = backPhotos[4] ?? null;

  // Пропорции превью берём от РЕАЛЬНОГО размера книги, а не от фикс. width/height,
  // иначе открытая обложка (2 стороны) выглядит вытянутой. width/height трактуем как
  // максимальный бокс, в который вписываем разворот с корректным соотношением сторон.
  const _wmm = Number(book.width_mm) || 210;
  const _hmm = Number(book.height_mm) || 297;
  const spreadAspect = (2 * _wmm) / _hmm; // ширина открытой обложки / высота
  let renderW = height * spreadAspect;
  let renderH = height;
  if (renderW > width) { renderW = width; renderH = width / spreadAspect; }
  renderW = Math.round(renderW);
  renderH = Math.round(renderH);
  const halfW = (renderW - 3) / 2; // 3px на корешок

  return (
    <Box sx={{
      width: renderW, height: renderH,
      borderRadius: "6px",
      overflow: "hidden",
      display: "flex",
      flexShrink: 0,
      boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
      border: "1px solid",
      borderColor: "divider",
    }}>
      {/* Задняя часть */}
      <Box sx={{
        width: halfW,
        bgcolor: bgColor,
        display: "flex", flexDirection: "column",
        alignItems: "center",
        p: "3px",
        pb: "8px",
        position: "relative",
        overflow: "hidden",
      }}>
        {/* Заголовок + год + сетка — только если задний макет НЕ "Только лого" */}
        {!backIsMinimal && (
          <>
            {/* Заголовок */}
            <Box sx={{
              height: "12.6%", width: "88%",
              display: "flex", flexDirection: "column",
              justifyContent: "flex-end", alignItems: "center",
            }}>
              {coverText && (
                <Box sx={{
                  fontSize: "3.5px", fontWeight: 800, color: titleColor,
                  textAlign: "center", lineHeight: 1.1,
                  textTransform: "uppercase", maxWidth: "100%",
                  overflow: "hidden", textOverflow: "ellipsis",
                  display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                }}>
                  {coverText}
                </Box>
              )}
            </Box>
            {coverYear && (
              <Box sx={{
                fontSize: "2.5px", fontWeight: 800, color: accentColor,
                textAlign: "center", lineHeight: 1, mt: "0.5px", mb: "1px",
              }}>
                {coverYear}
              </Box>
            )}
            {/* Наполнение задней по макету */}
            {backIsGrid ? (
              /* сетка 2×2 */
              <Box sx={{
                width: "66%", flex: 1, display: "grid",
                gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr 1fr", gap: "1px",
              }}>
                {[0, 1, 2, 3].map(idx => (
                  <Box key={idx} sx={{
                    borderRadius: "1px", overflow: "hidden",
                    bgcolor: backPhotos[idx] ? "transparent" : "rgba(255,255,255,0.12)",
                  }}>
                    {backPhotos[idx] && (
                      <Box component="img" src={backPhotos[idx]}
                        sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                    )}
                  </Box>
                ))}
              </Box>
            ) : backIsPhoto ? (
              /* Самостоятельное фото задней обложки — слот 5. */
              (() => {
                const url = backPhotos[5];
                return (
                  <Box sx={{
                    width: "68%", flex: 1, overflow: "hidden", borderRadius: "1px",
                    bgcolor: url ? "transparent" : "rgba(255,255,255,0.12)",
                  }}>
                    {url && (
                      <Box component="img" src={url}
                        sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                    )}
                  </Box>
                );
              })()
            ) : null /* text — без фото */}
          </>
        )}

        <Box sx={{
          position: "absolute", bottom: 1, left: 0, right: 0,
          display: "flex", justifyContent: "center",
        }}>
          <Box component="img" src="/logo.png" draggable={false} sx={{
            height: 5, width: "auto", maxWidth: "70%",
            display: "block", opacity: 0.85,
            pointerEvents: "none", userSelect: "none",
          }} />
        </Box>
      </Box>

      {/* Корешок */}
      <Box sx={{
        width: "3px", flexShrink: 0,
        background: "linear-gradient(90deg, rgba(0,0,0,0.12) 0%, rgba(255,255,255,0.3) 50%, rgba(0,0,0,0.08) 100%)",
      }} />

      {/* Передняя часть */}
      <Box sx={{
        width: halfW,
        bgcolor: bgColor,
        display: "flex", flexDirection: "column",
        alignItems: "center",
        p: "3px",
        pb: "8px",
        position: "relative",
        overflow: "hidden",
      }}>
        {/* Заголовок — прижат к низу блока */}
        <Box sx={{
          height: "12.6%", width: "88%",
          display: "flex", flexDirection: "column",
          justifyContent: "flex-end", alignItems: "center",
        }}>
          {coverText && (
            <Box sx={{
              fontSize: "3.5px", fontWeight: 800, color: titleColor,
              textAlign: "center", lineHeight: 1.1,
              textTransform: "uppercase", maxWidth: "100%",
              overflow: "hidden", textOverflow: "ellipsis",
              display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
            }}>
              {coverText}
            </Box>
          )}
        </Box>
        {coverYear && (
          <Box sx={{
            fontSize: "2.5px", fontWeight: 800, color: accentColor,
            textAlign: "center", lineHeight: 1, mt: "0.5px", mb: "1px",
          }}>
            {coverYear}
          </Box>
        )}
        {frontIsPhoto ? (
          /* Макет "Фото": фото передней обложки (портретная зона по центру) */
          <Box sx={{
            flex: 1, width: "68%",
            display: "flex", alignItems: "center", justifyContent: "center",
            overflow: "hidden", borderRadius: "1px",
            bgcolor: frontPhotoUrl ? "transparent" : "rgba(255,255,255,0.12)",
            border: frontPhotoUrl ? "none" : "0.5px dashed rgba(255,255,255,0.3)",
          }}>
            {frontPhotoUrl && (
              <Box component="img" src={frontPhotoUrl}
                sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            )}
          </Box>
        ) : stickerUrl ? (
          /* Макет "Стикер": стикер по центру */
          <Box sx={{
            flex: 1, width: "65%",
            display: "flex", alignItems: "center", justifyContent: "center",
            overflow: "hidden",
          }}>
            <Box component="img" src={stickerUrl}
              sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", display: "block" }} />
          </Box>
        ) : (
          <Box sx={{
            flex: 1, width: "65%",
            borderRadius: "1px",
            border: "0.5px dashed rgba(255,255,255,0.3)",
          }} />
        )}
        <Box sx={{
          position: "absolute", bottom: 1, left: 0, right: 0,
          display: "flex", justifyContent: "center",
        }}>
          <Box component="img" src="/logo.png" draggable={false} sx={{
            height: 5, width: "auto", maxWidth: "70%",
            display: "block", opacity: 0.85,
            pointerEvents: "none", userSelect: "none",
          }} />
        </Box>
      </Box>
    </Box>
  );
}


// ─── Карточка книги (десктоп) ────────────────────────────────────────────────

function BookRow({ book, isCurrent, onSelect, onDelete, isChecked, onToggleCheck, onRename, onChangeSize, onClone, cloning, showClone }) {
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(book.title || "");

  const spreadsCount = book.spreads?.length ?? 0;
  const pagesCount = spreadsCount > 2 ? (spreadsCount - 2) * 2 : 0;
  const accent = book.cover_bg_color || "#834A90";

  const date = book.updated_at
    ? new Date(book.updated_at).toLocaleDateString("ru-RU", {
        day: "numeric", month: "long", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      })
    : "";

  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: "16px",
        overflow: "hidden",
        bgcolor: isCurrent
          ? "color-mix(in srgb, var(--joy-palette-primary-500) 5%, transparent)"
          : "background.surface",
        border: "1.5px solid",
        borderColor: isCurrent
          ? "color-mix(in srgb, var(--joy-palette-primary-400) 45%, transparent)"
          : "divider",
        boxShadow: isCurrent ? "0 6px 22px rgba(0,0,0,0.08)" : "0 2px 12px rgba(0,0,0,0.04)",
        transition: "all 0.18s",
        "&:hover": isCurrent ? {} : {
          borderColor: "color-mix(in srgb, var(--joy-palette-primary-400) 35%, transparent)",
          boxShadow: "0 4px 18px rgba(0,0,0,0.08)",
        },
      }}
    >
      {/* Акцентная полоса цвета обложки */}
      <Box sx={{
        position: "absolute", left: 0, top: 0, bottom: 0, width: 6,
        background: `linear-gradient(180deg, ${accent}, color-mix(in srgb, ${accent} 60%, #000))`,
      }} />

      {/* Строка 1 */}
      <Box
        onClick={() => !isCurrent && onSelect(book)}
        sx={{
          display: "flex", alignItems: "center", gap: 2,
          pl: 2.5, pr: 2, pt: 2, pb: 1.5,
          cursor: isCurrent ? "default" : "pointer",
        }}
      >
        <Checkbox
          label="Выбрать"
          checked={isChecked}
          onChange={(e) => { e.stopPropagation(); onToggleCheck(book.id); }}
          onClick={(e) => e.stopPropagation()}
          size="sm"
          sx={{ flexShrink: 0 }}
        />

        <Box sx={{
          flexShrink: 0, p: "4px", borderRadius: "12px",
          bgcolor: `color-mix(in srgb, ${accent} 14%, transparent)`,
        }}>
          <MiniCoverPreview book={book} width={96} height={122} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" gap={1}>
            {editing ? (
              <Input
                size="sm"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { onRename(book.id, editTitle.trim() || "Без названия"); setEditing(false); }
                  if (e.key === "Escape") { setEditTitle(book.title || ""); setEditing(false); }
                }}
                onClick={(e) => e.stopPropagation()}
                autoFocus
                sx={{ fontWeight: 700, fontSize: "15px", flex: 1, borderRadius: "8px" }}
                endDecorator={
                  <IconButton size="sm" variant="plain" color="success"
                    onClick={(e) => { e.stopPropagation(); onRename(book.id, editTitle.trim() || "Без названия"); setEditing(false); }}
                    sx={{ borderRadius: "6px" }}>
                    <CheckRoundedIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                }
              />
            ) : (
              <>
                <Typography level="title-md"
                  sx={{ fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: "0 1 auto" }}>
                  {book.title || "Без названия"}
                </Typography>
                <IconButton size="sm" variant="plain" color="neutral"
                  onClick={(e) => { e.stopPropagation(); setEditTitle(book.title || ""); setEditing(true); }}
                  sx={{ borderRadius: "6px", minWidth: 26, minHeight: 26 }}>
                  <EditRoundedIcon sx={{ fontSize: 15 }} />
                </IconButton>
                {isCurrent && (
                  <Chip size="sm" variant="soft" color="primary"
                    sx={{ fontSize: "10px", fontWeight: 700, height: 20, flexShrink: 0 }}>
                    Текущая
                  </Chip>
                )}
              </>
            )}
          </Stack>

          <Stack direction="row" gap={2} mt={0.5}>
            <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
              {bookFormatLabel(book)}
            </Typography>
            <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
              {pagesCount} {pagesCount < 5 ? "страницы" : "страниц"}
            </Typography>
          </Stack>
          {date && (
            <Typography level="body-xs" sx={{ color: "text.tertiary", fontSize: "11px", mt: 0.5 }}>
              Изменена: {date}
            </Typography>
          )}
        </Box>

        {!isCurrent && (
          <IconButton size="sm" variant="plain" color="danger"
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm("Удалить эту книгу? Это действие необратимо.")) onDelete(book.id);
            }}
            sx={{ borderRadius: "10px", flexShrink: 0 }}>
            <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </Box>

      {/* Строка 2: кнопки */}
      <Box sx={{ display: "flex", gap: 1, px: 2.5, pb: 2, pt: 0.25 }}>
        <Button
          size="sm" variant="soft" color="neutral"
          startDecorator={<AspectRatioRoundedIcon sx={{ fontSize: 16 }} />}
          onClick={(e) => { e.stopPropagation(); onChangeSize?.(book); }}
          sx={{ flex: 1, borderRadius: "10px", fontSize: "13px", fontWeight: 700, "&:hover": { transform: "none" } }}
        >
          Размер
        </Button>
        {showClone && (
          <Button
            size="sm" variant="soft" color="success" loading={cloning}
            startDecorator={<ContentCopyRoundedIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => { e.stopPropagation(); onClone?.(book.id); }}
            sx={{ flex: 1, borderRadius: "10px", fontSize: "13px", fontWeight: 700, "&:hover": { transform: "none" } }}
          >
            Дублировать
          </Button>
        )}
        <Button
          size="sm" variant="solid" color="primary"
          startDecorator={<OpenInNewRoundedIcon sx={{ fontSize: 16 }} />}
          onClick={(e) => { e.stopPropagation(); onSelect(book); }}
          disabled={isCurrent}
          sx={{ flex: 1, borderRadius: "10px", fontSize: "13px", fontWeight: 700, "&:hover": { transform: "none" } }}
        >
          {isCurrent ? "Открыта" : "Открыть"}
        </Button>
      </Box>
    </Box>
  );
}


// ─── Пустое состояние ────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <Stack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
      <PhotoLibraryRoundedIcon sx={{ fontSize: 56, color: "neutral.300", mb: 2 }} />
      <Typography level="body-md" sx={{ color: "text.secondary", textAlign: "center", fontWeight: 600 }}>
        У вас пока нет сохранённых книг
      </Typography>
      <Typography level="body-sm" sx={{ color: "text.tertiary", textAlign: "center", mt: 0.5 }}>
        Создайте первую книгу, и она появится здесь.
      </Typography>
    </Stack>
  );
}


// ─── Главный компонент ────────────────────────────────────────────────────────

export default function ModalMyBooks({ open, onClose, onSelectBook, onChangeSize, onCheckout }) {
  const { bookId, savedBookIds, setSavedBookIds, addSavedBookId, cartItems, setCartItems, selectedBookIds, setSelectedBookIds, setShowCartModal, setShowMyBooksModal } = useContext(LayoutContext);

  const [books, setBooks]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [cloningId, setCloningId] = useState(null);
  const [infoOpen, setInfoOpen] = useState(false);

  const handleClone = async (id) => {
    if (cloningId) return;
    setCloningId(id);
    try {
      const clone = await bookApi.cloneBook(id);
      // Добавляем клон в список сохранённых книг → окно перефетчит и покажет его.
      if (clone?.id) addSavedBookId(clone.id);
    } catch (e) {
      window.alert("Не удалось клонировать книгу. Попробуйте ещё раз.");
    } finally {
      setCloningId(null);
    }
  };

  // Загружаем книги при открытии
  useEffect(() => {
    if (!open) return;

    if (savedBookIds.length === 0) {
      setBooks([]);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const loadBooks = async () => {
      const results = [];
      for (const id of savedBookIds) {
        try {
          const book = await bookApi.getBook(id);
          // Заказанные книги не показываем в «Мои книги». id при этом
          // оставляем в savedBookIds — книга привязана к клиенту через заказ.
          if (book?.is_ordered && !isAdminViewMode()) continue;
          if (!cancelled) results.push(book);
        } catch (err) {
          // 404 — книги нет; 410 — медиафайлы книги очищены воркером (Правка 4).
          // В обоих случаях книга больше не редактируется — убираем из «Мои книги».
          // На прочих (сетевых/временных) ошибках книгу НЕ удаляем, иначе из-за
          // случайного сбоя потеряем ещё живые книги.
          if (err?.status === 404 || err?.status === 410) {
            console.warn(
              err?.status === 410
                ? `Книга ${id}: медиа очищены (410) — убираю из «Мои книги»`
                : `Книга ${id} не найдена (404) — убираю из сохранённых`
            );
            setSavedBookIds(prev => prev.filter(x => x !== id));
          } else {
            console.warn(`Книга ${id}: ошибка загрузки (${err?.status || "сеть"}) — оставляю в списке`);
          }
        }
      }
      if (!cancelled) {
        results.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
        // Синхронизируем selectedBookIds из серверного in_cart
        const idsInCart = results.filter(b => b.in_cart).map(b => b.id);
        const idsNotInCart = results.filter(b => !b.in_cart).map(b => b.id);
        setSelectedBookIds(prev => {
          let updated = new Set(prev);
          // Добавляем те, что in_cart на сервере
          idsInCart.forEach(id => updated.add(id));
          // Убираем те, что НЕ in_cart на сервере
          idsNotInCart.forEach(id => updated.delete(id));
          return [...updated];
        });
        
        setBooks(results);
        setLoading(false);
      }
    };

    loadBooks();
    return () => { cancelled = true; };
  }, [open, savedBookIds]);


  const handleDelete = async (id) => {
    // Удаляем из корзины на сервере
    const cartItem = cartItems.find(item => item.bookId === id);
    if (cartItem?.cartItemId) {
      bookApi.removeFromCart(cartItem.cartItemId).catch(err =>
        console.error("Ошибка удаления из корзины:", err.message)
      );
    }

    try {
      await bookApi.deleteBook(id);
    } catch (err) {
      console.error("Ошибка удаления книги:", err.message);
    }

    setSavedBookIds(prev => prev.filter(x => x !== id));
    setBooks(prev => prev.filter(b => b.id !== id));
    setCartItems(prev => prev.filter(item => item.bookId !== id));
    setSelectedBookIds(prev => prev.filter(x => x !== id));

    if (id === bookId) {
      onSelectBook(null);
    }
  };

  const handleToggleCheck = async (id) => {
    const wasChecked = selectedBookIds.includes(id);

    if (wasChecked) {
      // ── Убираем галочку ──
      // Удаляем CartItem если есть
      const cartItem = cartItems.find(item => item.bookId === id);
      if (cartItem?.cartItemId) {
        bookApi.removeFromCart(cartItem.cartItemId).catch(err =>
          console.error("Ошибка удаления из корзины:", err.message)
        );
      }
      // Ставим in_cart = false
      bookApi.updateBook(id, { inCart: false }).catch(err =>
        console.error("Ошибка обновления in_cart:", err.message)
      );
      setCartItems(cart => cart.filter(item => item.bookId !== id));
      setSelectedBookIds(prev => prev.filter(x => x !== id));
    } else {
      // ── Ставим галочку ──
      // ТОЛЬКО обновляем in_cart, НЕ создаём CartItem!
      try {
        await bookApi.updateBook(id, { inCart: true });

        const book = books.find(b => b.id === id);
        if (book) {
          const coverPhoto = book.spreads?.[0]?.photos?.find(p => p.side === "left");
          const coverUrl = coverPhoto?.image_url ?? book.cover_template_data?.image_url ?? null;
          const alreadyInCart = cartItems.some(item => item.bookId === id);
          if (!alreadyInCart) {
            setCartItems(cart => [...cart, {
              title: book.title || "Фотокнига",
              pagesCount: (book.spreads?.length ?? 2) > 2 ? (book.spreads.length - 2) * 2 : 0,
              size: `${book.width_mm}×${book.height_mm} мм`,
              coverUrl,
              price: book.price ?? null,
              bookId: id,
              bookFormatId: book.book_format ?? null,
              widthMm: book.width_mm,
              heightMm: book.height_mm,
              cartItemId: null,  // CartItem ещё нет — создастся при validate
              bookData: book,
            }]);
          }
        }
        setSelectedBookIds(prev => [...prev, id]);
      } catch (err) {
        console.error("Ошибка обновления in_cart:", err.message);
        alert("Не удалось обновить статус книги: " + (err.message || ""));
      }
    }
  };

  const handleGoToCheckout = () => {
    onClose();
    setTimeout(() => {
      if (onCheckout) {
        onCheckout();
      } else {
        setShowCartModal(true);
      }
    }, 200);
  };

  const handleRename = async (id, newTitle) => {
    try {
      await bookApi.updateBook(id, { title: newTitle });
      setBooks(prev => prev.map(b => b.id === id ? { ...b, title: newTitle } : b));
    } catch (err) {
      console.error("Ошибка переименования:", err.message);
      alert(err.message || "Не удалось сохранить название");
      // Сбрасываем заголовок обратно на прежний
      // (books уже содержат старое значение — ничего менять не нужно)
    }
  };

  const handleSelect = (book) => {
    onSelectBook(book);
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <Modal
          sx={{ zIndex: 10012 }}
          open={open}
          // onClose={onClose}
          slotProps={{
            backdrop: {
              component: motion.div,
              initial: { opacity: 0, backdropFilter: "blur(0px)" },
              animate: { opacity: 1, backdropFilter: "blur(8px)" },
              exit: { opacity: 0, backdropFilter: "blur(0px)" },
              transition: { duration: 0.3, ease: "easeInOut" },
            },
          }}
        >
          <ModalOverflow sx={{ height: "auto", p: 0 }}>
            <ModalDialog
              size="lg"
              component={motion.div}
              initial={{ opacity: 0, y: -10, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.98 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              sx={(theme) => ({
                width: "760px",
                maxHeight: "75vh",
                p: 0,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                [theme.breakpoints.down("sm")]: {
                  width: "100vw",
                  minWidth: "100vw",
                  minHeight: "100vh",
                  m: 0,
                  borderRadius: 0,
                },
              })}
            >
              {/* ── Заголовок ── */}
              <Box
                sx={{
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 3, py: 2.5,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Box>
                  <Typography level="h4" sx={{ fontWeight: 800, letterSpacing: "-0.02em" }}>
                    Мои книги
                  </Typography>
                  <Typography level="body-xs" sx={{ color: "text.tertiary", mt: 0.25 }}>
                    {savedBookIds.length > 0
                      ? `${books.length} ${books.length === 1 ? "книга" : "книг"} сохранено`
                      : "Нет сохранённых книг"}
                  </Typography>
                </Box>
                <Stack direction="row" gap={0.5} alignItems="center">
                  <IconButton
                    size="sm"
                    variant="plain"
                    color="neutral"
                    onClick={() => setInfoOpen(true)}
                    sx={{ borderRadius: "10px" }}
                  >
                    <HelpOutlineRoundedIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                  <IconButton
                    size="sm"
                    variant="plain"
                    color="neutral"
                    onClick={onClose}
                    sx={{ borderRadius: "10px" }}
                  >
                    <CloseRoundedIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Stack>
              </Box>

              {/* ── Список книг ── */}
              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  px: 2.5, py: 2,
                  "&::-webkit-scrollbar": { width: "4px" },
                  "&::-webkit-scrollbar-thumb": {
                    borderRadius: "4px",
                    bgcolor: "color-mix(in srgb, var(--joy-palette-neutral-500) 20%, transparent)",
                  },
                }}
              >
                {loading ? (
                  <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
                    <CircularProgress size="md" />
                    <Typography level="body-sm" sx={{ color: "text.tertiary", mt: 1.5 }}>
                      Загружаем книги…
                    </Typography>
                  </Stack>
                ) : books.length === 0 ? (
                  <EmptyState />
                ) : (
                  <Stack gap={1.25}>
                    {books.map(book => (
                      <BookRow
                        key={book.id}
                        book={book}
                        isCurrent={book.id === bookId}
                        onSelect={handleSelect}
                        onDelete={handleDelete}
                        isChecked={selectedBookIds.includes(book.id)}
                        onToggleCheck={handleToggleCheck}
                        onRename={handleRename}
                        onChangeSize={onChangeSize}
                        onClone={handleClone}
                        cloning={cloningId === book.id}
                        showClone={true}
                      />
                    ))}
                  </Stack>
                )}

              </Box>

              {/* ── Нижняя панель ── */}
              <Box sx={{ flexShrink: 0, px: 3, py: 2, borderTop: "1px solid", borderColor: "divider" }}>
                <Stack gap={1}>
                  <Button
                    fullWidth size={selectedBookIds.length > 0 ? "md" : "lg"}
                    variant="outlined" color="primary"
                    onClick={() => {
                      onClose();
                      setTimeout(() => onSelectBook(null), 200);
                    }}
                    sx={{ borderRadius: "14px", fontWeight: 700 }}
                  >
                    + Создать новую книгу
                  </Button>
                  
                  <Button
                    fullWidth size="md" variant="solid" color="primary"
                    startDecorator={<ShoppingCartCheckoutRoundedIcon />}
                    onClick={handleGoToCheckout}
                    disabled={selectedBookIds.length == 0}
                    sx={{ borderRadius: "12px", fontWeight: 700 }}
                  >
                    Оформить заказ ({selectedBookIds.length})
                  </Button>
                  { selectedBookIds.length == 0 && (
                    <Typography level="body-xs" sx={{ textAlign: 'center' }}>
                      Выберите галочками книги, которые хотите заказать
                    </Typography>
                  )}
                </Stack>
              </Box>

              <BookActionsInfoModal open={infoOpen} onClose={() => setInfoOpen(false)} />
            </ModalDialog>
          </ModalOverflow>
        </Modal>
      )}
    </AnimatePresence>
  );
}